// The six types the Support work list manages. 'Comment' and 'Focus Item' are
// deliberately absent: Comments are email-thread children (0 due dates, mostly
// parented) and Focus Items carry no due dates or parents. Neither is managed
// as work, so neither belongs in a list whose job is "what do I close next".
export const WORK_TYPE_NAMES = [
  'Epic',
  'Feature',
  'Requirement',
  'Fast-Follow',
  'Bug',
  'Support Ticket',
];

export const WORK_TYPES = new Set(WORK_TYPE_NAMES);

// The collapsed type chips above the work list. Same vocabulary and shape as
// Support_ServiceDesk's collapsedTypeChips so the two surfaces read alike; an
// empty selection means "All".
//
// The two groups PARTITION all six work types -- selecting both is exactly
// "All". Requirement and Fast-Follow live under Feature Requests because that
// is what they are: VALID_PARENT_TYPES puts both under Feature, so they are the
// delivery half of a feature request, not a separate kind of work. Leaving them
// out of both chips (the original split) meant "All" quietly showed rows no
// chip could reach, and picking both chips silently dropped them.
export const TYPE_CHIP_GROUPS = [
  {
    key: 'feature_group',
    label: 'Feature Requests',
    types: ['Epic', 'Feature', 'Requirement', 'Fast-Follow'],
  },
  { key: 'bugs_support', label: 'Bugs & Support', types: ['Bug', 'Support Ticket'] },
];

// Hierarchy rules: which types can be parents of which.
//
// Bug and 'Support Ticket' gained Epic/Feature parents because that is already
// Irene's practice -- 27 open Bugs/Tickets are Epic-parented in production, and
// before this the create modal and detail drawer offered them no legal parent
// at all.
export const VALID_PARENT_TYPES = {
  Epic: ['Epic'],
  Feature: ['Epic'],
  Requirement: ['Feature', 'Requirement', 'Fast-Follow'],
  'Fast-Follow': ['Feature'],
  Bug: ['Epic', 'Feature'],
  'Support Ticket': ['Epic', 'Feature'],
};

// Hierarchy rules: which child types are allowed under a parent. The inverse of
// VALID_PARENT_TYPES, minus Epic-under-Epic: nesting an Epic is legal when you
// reparent an existing one, but it is not offered as a child type to create.
export const CHILD_TYPES_FOR_PARENT = {
  Epic: ['Feature', 'Bug', 'Support Ticket'],
  Feature: ['Requirement', 'Fast-Follow', 'Bug', 'Support Ticket'],
  Requirement: ['Requirement'],
  'Fast-Follow': ['Requirement'],
};

// True when `childType` may legally sit under `parentType`. Single source of
// truth for the create modal, the detail drawer and the list's bulk reparent
// picker, so a merged list can't make an illegal reparent a two-click accident.
export function canParent(parentTypeName, childTypeName) {
  const validParents = VALID_PARENT_TYPES[childTypeName];
  if (!validParents) return true;
  return validParents.includes(parentTypeName);
}

// Quick-add: resolve child type from requirementTypes array at runtime
export function getChildType(parentTypeName, requirementTypes) {
  const mapping = {
    Epic: 'Feature',
    Feature: 'Requirement',
    Requirement: 'Requirement',
    'Fast-Follow': 'Requirement',
  };
  const childName = mapping[parentTypeName] || 'Requirement';
  const type = requirementTypes.find((t) => t.name === childName);
  return {
    name: childName.toLowerCase(),
    typeId: type?.id || null,
  };
}

// Display name for quick-add placeholder
export const CHILD_TYPE_LABELS = {
  Epic: 'feature',
  Feature: 'requirement',
  Requirement: 'sub-requirement',
  'Fast-Follow': 'requirement',
};

// Badge classes for requirement types
export const TYPE_CLASSES = {
  Epic: 'bg-indigo-50 text-indigo-700',
  Feature: 'bg-blue-50 text-blue-700',
  Requirement: 'bg-emerald-50 text-emerald-700',
  'Fast-Follow': 'bg-violet-50 text-violet-700',
  Bug: 'bg-red-50 text-red-700',
  'Support Ticket': 'bg-amber-50 text-amber-700',
};

// Badge classes for statuses
export const STATUS_CLASSES = {
  draft: 'bg-gray-100 text-gray-600',
  approved: 'bg-blue-50 text-blue-700',
  in_progress: 'bg-amber-50 text-amber-700',
  done: 'bg-emerald-50 text-emerald-700',
  open: 'bg-red-50 text-red-700',
  waiting: 'bg-orange-50 text-orange-700',
  backlog: 'bg-gray-100 text-gray-600',
  resolved: 'bg-emerald-50 text-emerald-700',
  closed: 'bg-gray-100 text-gray-500',
  not_relevant: 'bg-gray-100 text-gray-500',
  archived: 'bg-gray-100 text-gray-400',
};

// Display labels for statuses.
//
// 'backlog' is not a status the Work list offers or a staff user can assign --
// it is in HADES_SCOPE_STATUSES on the backend and appears on Hades board rows,
// which filter on it. It lives here rather than in a second Hades-local status
// map, because a second map is how the same status ends up with two spellings.
export const STATUS_LABELS = {
  draft: 'Draft',
  approved: 'Approved',
  in_progress: 'In Progress',
  done: 'Done',
  open: 'Open',
  waiting: 'Waiting',
  backlog: 'Backlog',
  resolved: 'Resolved',
  closed: 'Closed',
  not_relevant: 'Not Relevant',
  archived: 'Archived',
};

// Statuses a staff user may assign. 'resolved' is DEPRECATED (Irene, Jul 16):
// 'done' = completed, 'closed' = other closeout. We keep 'resolved' in the
// display maps above so legacy / not-yet-migrated rows still render, but it is no
// longer offered as a choice anywhere. See DEPRECATED_STATUSES.
export const DEPRECATED_STATUSES = new Set(['resolved']);

export function isSelectableStatus(status) {
  return !DEPRECATED_STATUSES.has(status);
}

// Statuses a staff user may assign, in workflow order. Single source of truth
// for every status picker (the table's bulk menu, the drawer's dropdown), so
// the two can't drift. 'draft' is excluded deliberately: it's an authoring
// state, not something you triage a ticket into.
export const SELECTABLE_STATUSES = ['open', 'approved', 'in_progress', 'waiting', 'done', 'closed']
  .filter(isSelectableStatus);

// Terminal / completed statuses. Includes the deprecated 'resolved' so legacy
// rows are still categorized as done until the backend migration lands.
export const DONE_STATUSES = new Set(['done', 'resolved', 'closed', 'not_relevant', 'archived']);

// Server-side terminal set (models.Requirement.DONE_STATUSES plus archived).
// A list fetched with open_only=true holds none of these, so any view that
// wants to show one has to fetch without that flag.
export const SERVER_DONE_STATUSES = new Set(['done', 'closed', 'not_relevant', 'archived']);

export function awaitingStaffReply(item) {
  return item?.awaiting_staff_reply === true;
}

export function customerRepliedLabel(item) {
  const days = daysSince(item?.last_customer_reply_at);
  if (days === null || days < 0) return 'Customer replied, no reply from us yet';
  const when = days === 0 ? 'today' : days === 1 ? 'yesterday' : `${days} days ago`;
  return `Customer replied ${when}, no reply from us yet`;
}

// Reorder helper: the user dragged a SUBSET of a parent's children (whatever
// the filters left visible). Re-rank the full sibling set so the visible rows
// take the dragged order and the hidden rows keep their relative places.
export function mergeSiblingOrder(allSiblings, visibleOrderIds) {
  const visible = new Set(visibleOrderIds);
  const queue = [...visibleOrderIds];
  const merged = [];
  for (const sibling of allSiblings) {
    if (visible.has(sibling.id)) merged.push(queue.shift());
    else merged.push(sibling.id);
  }
  return merged.map((id, position) => ({ id, position }));
}

// ---------------------------------------------------------------------------
// Priority — single source of truth (Item 1).
// Backend stores p0/p1/p2/p3/none. The rating widget uses numeric 0-3. There is
// NO p4 (the old "P4" control was a phantom write bug). Legacy string rows
// (urgent/high/medium/low) are normalized on read via priorityToCode().
// ---------------------------------------------------------------------------
export const PRIORITY_CODES = ['p0', 'p1', 'p2', 'p3', 'none'];

export const PRIORITY_INT_TO_CODE = { 0: 'p0', 1: 'p1', 2: 'p2', 3: 'p3' };
export const PRIORITY_CODE_TO_INT = { p0: 0, p1: 1, p2: 2, p3: 3, none: null };

// Decided label style: "P0 Critical / P1 High / P2 Medium / P3 Low".
export const PRIORITY_LABELS = {
  p0: 'P0 Critical',
  p1: 'P1 High',
  p2: 'P2 Medium',
  p3: 'P3 Low',
  none: 'None',
};

// Compact labels for dense table cells / rating buttons.
export const PRIORITY_SHORT_LABELS = {
  p0: 'P0',
  p1: 'P1',
  p2: 'P2',
  p3: 'P3',
  none: '—',
};

export const PRIORITY_CLASSES = {
  p0: 'bg-red-100 text-red-700',
  p1: 'bg-orange-100 text-orange-700',
  p2: 'bg-amber-100 text-amber-700',
  p3: 'bg-gray-200 text-gray-600',
  none: 'bg-gray-100 text-gray-400',
};

// Numeric levels for the P0-P3 rating widget (no P4).
export const PRIORITY_LEVELS = [
  { value: 0, code: 'p0', label: 'P0', activeClass: PRIORITY_CLASSES.p0 },
  { value: 1, code: 'p1', label: 'P1', activeClass: PRIORITY_CLASSES.p1 },
  { value: 2, code: 'p2', label: 'P2', activeClass: PRIORITY_CLASSES.p2 },
  { value: 3, code: 'p3', label: 'P3', activeClass: PRIORITY_CLASSES.p3 },
];

// Sort ordering: p0 highest, unset last.
export const PRIORITY_SORT_ORDER = { p0: 0, p1: 1, p2: 2, p3: 3, none: 4 };

// Legacy string vocabulary still present in a few old rows / surfaces.
const LEGACY_PRIORITY_TO_CODE = {
  urgent: 'p0',
  high: 'p1',
  medium: 'p2',
  low: 'p3',
};

// Normalize any priority value (int 0-3, code p0-p3/none, legacy string, or
// null/blank) to a canonical p-code.
export function priorityToCode(value) {
  if (value === null || value === undefined || value === '') return 'none';
  if (typeof value === 'number') return PRIORITY_INT_TO_CODE[value] || 'none';
  const v = String(value).toLowerCase();
  if (v in LEGACY_PRIORITY_TO_CODE) return LEGACY_PRIORITY_TO_CODE[v];
  if (v in PRIORITY_CODE_TO_INT) return v;
  return 'none';
}

// Canonical numeric level (0-3) or null, for the rating widget's modelValue.
export function priorityInt(value) {
  return PRIORITY_CODE_TO_INT[priorityToCode(value)];
}

export function priorityLabel(value) {
  return PRIORITY_LABELS[priorityToCode(value)] || 'None';
}

export function priorityShortLabel(value) {
  return PRIORITY_SHORT_LABELS[priorityToCode(value)] || '—';
}

export function priorityClass(value) {
  return PRIORITY_CLASSES[priorityToCode(value)] || PRIORITY_CLASSES.none;
}

export function prioritySortValue(value) {
  return PRIORITY_SORT_ORDER[priorityToCode(value)] ?? 5;
}

// Customer-facing (non-superuser) view collapses the granular staff statuses
// into four friendly buckets (row 1535). Staff keep the granular labels above.
//
// 'waiting' is its own bucket, not part of 'in_progress'. The two differ in
// OWNERSHIP, not progress: 'in_progress' is our ball, 'waiting' is theirs. Rolled
// into "In Progress" a blocked ticket read as "Kairon is working on this" while
// nothing could happen until the customer replied. Progress is still three
// stages — see customerSteps() in Support_ItemDetail.vue, which maps 'waiting'
// onto the middle stage rather than adding a fourth stepper node.
export const CUSTOMER_STATUS_BUCKETS = {
  draft: 'received',
  open: 'received',
  approved: 'received',
  in_progress: 'in_progress',
  waiting: 'waiting',
  done: 'resolved',
  resolved: 'resolved',
  closed: 'resolved',
  not_relevant: 'resolved',
  archived: 'resolved',
};

export const CUSTOMER_STATUS_LABELS = {
  received: 'Received',
  in_progress: 'In Progress',
  waiting: 'Needs Input',
  resolved: 'Resolved',
};

// 'waiting' is deliberately a full step away from in_progress's amber — the
// whole point of the bucket is that a customer can tell the two apart at a
// glance.
export const CUSTOMER_STATUS_CLASSES = {
  received: 'bg-blue-50 text-blue-700',
  in_progress: 'bg-amber-50 text-amber-700',
  waiting: 'bg-orange-100 text-orange-800',
  resolved: 'bg-green-50 text-green-700',
};

export const CUSTOMER_STATUS_ORDER = ['received', 'in_progress', 'waiting', 'resolved'];

// One plain-language sentence per bucket. Used as the tooltip AND the
// accessible name on customer status chips, so the meaning of a chip never
// depends on its colour — which matters most for 'waiting', the only bucket
// that asks the reader to do something.
export const CUSTOMER_STATUS_HINTS = {
  received: 'We have your request and will pick it up shortly.',
  in_progress: 'Our team is working on this.',
  waiting: 'We need information from you before we can continue.',
  resolved: 'This is finished. Reply if anything still looks wrong.',
};

export function customerStatusHint(status) {
  return CUSTOMER_STATUS_HINTS[customerBucket(status)] || '';
}

// Maps any status (raw staff status or an already-bucketed value) to its
// customer bucket, so callers can pass either.
//
// CUSTOMER_STATUS_BUCKETS is the source of truth. The fallback to 'received' is
// a safety net: a raw internal status token must never render to a customer, so
// anything we don't recognize (a new backend status, a legacy row) reads as
// "Received" rather than leaking e.g. "approved". See CUSTOMER_STATUS_ORDER.
export function customerBucket(status) {
  if (CUSTOMER_STATUS_ORDER.includes(status)) return status;
  return CUSTOMER_STATUS_BUCKETS[status] || 'received';
}

export function customerStatusLabel(status) {
  return CUSTOMER_STATUS_LABELS[customerBucket(status)] || status;
}

export function customerStatusClass(status) {
  return CUSTOMER_STATUS_CLASSES[customerBucket(status)] || 'bg-gray-100 text-gray-500';
}

// Customer-facing (non-staff) view collapses internal-sounding backlog
// jargon ("Epic") into plain-language labels. Staff keep the raw type name.
export const CUSTOMER_TYPE_LABELS = {
  Epic: 'Feature Request',
  Feature: 'Feature Request',
  Bug: 'Bug Report',
  'Support Ticket': 'Support Ticket',
};

export function customerTypeLabel(typeName) {
  return CUSTOMER_TYPE_LABELS[typeName] || typeName;
}

// ---------------------------------------------------------------------------
// Dates.
//
// `due_date` is a bare calendar day ("2026-08-14"), NOT an instant -- Aug 14 is
// Aug 14 in every timezone. `new Date('2026-08-14')` parses it as UTC midnight
// per the ES spec, so toLocaleDateString() renders "Aug 13" for every viewer
// west of UTC, and any diff against a local midnight is off by the UTC offset
// (which is how "Due today" rendered as "Yesterday" and overdue styling fired a
// day early). The fix is not to convert it to some other timezone -- it's to
// not convert a timezone-less value at all.
//
// Full ISO timestamps (created_at, updated_at, first_response_at, ...) ARE
// instants and must keep rendering in the viewer's local zone. toLocalDate()
// tells the two apart so callers don't have to.
// ---------------------------------------------------------------------------
const DATE_ONLY_RE = /^\d{4}-\d{2}-\d{2}$/;

export function toLocalDate(value) {
  if (!value) return null;
  if (value instanceof Date) return value;
  const s = String(value);
  if (DATE_ONLY_RE.test(s)) {
    // Local midnight on that calendar day — no conversion applied.
    const [y, m, d] = s.split('-').map(Number);
    return new Date(y, m - 1, d);
  }
  const parsed = new Date(s);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

const DEFAULT_DATE_OPTS = { month: 'short', day: 'numeric', year: 'numeric' };

export function formatDateValue(value, opts) {
  const d = toLocalDate(value);
  if (!d) return '';
  return d.toLocaleDateString('en-US', opts || DEFAULT_DATE_OPTS);
}

// Whole calendar days from today to `value`; negative means overdue, 0 is
// today. Both sides are normalized to local midnight, so DST-length days
// (23h/25h) still come out as whole days.
export function daysUntil(value) {
  const d = toLocalDate(value);
  if (!d) return null;
  const now = new Date();
  const from = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const to = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  return Math.round((to - from) / 86400000);
}

// Whole calendar days elapsed since `value`; 0 is today, null if unparseable.
// Inverse of daysUntil(), same local-midnight normalization, so a future value
// comes back negative rather than as a bogus 0.
export function daysSince(value) {
  const days = daysUntil(value);
  return days === null ? null : -days;
}

// "6 days" / "1 day" / "today" — the elapsed-time half of the ownership bar's
// "since Aug 1 · 6 days". Returns '' when the value is missing or in the
// future, so callers can v-if on it (waiting_since is optional: it ships in a
// parallel backend PR and is permanently null for pre-migration tickets).
export function daysAgoLabel(value) {
  const days = daysSince(value);
  if (days === null || days < 0) return '';
  if (days === 0) return 'today';
  return days === 1 ? '1 day' : `${days} days`;
}

// True when `value` falls within the current Mon–Fri business week and is not
// already overdue. A date that is today or in the past returns false (those get
// their own overdue / "today" styling).
export function isDueThisBusinessWeek(value) {
  const days = daysUntil(value);
  if (days == null || days <= 0) return false;
  const today = new Date();
  const dow = today.getDay(); // 0=Sun … 6=Sat
  // Days remaining until end-of-Friday: Sun=5, Mon=4, Tue=3, Wed=2, Thu=1, Fri=0, Sat=-1
  const daysUntilFriday = 5 - dow;
  return daysUntilFriday > 0 && days <= daysUntilFriday;
}

// Compact form for dense table cells — "6d". Empty when unavailable.
export function daysAgoShortLabel(value) {
  const days = daysSince(value);
  if (days === null || days < 0) return '';
  return `${days}d`;
}

// Millisecond timestamp of the LOCAL calendar day `value` falls on, or null.
//
// The scalar date filters (on_or_before / on_or_after) compare whole calendar
// days, never instants: `due_date` is a bare day and `created_at` / `updated_at`
// are instants, but a user picking a day in a date input means the same thing
// for both. Routing both sides through toLocalDate() and then flattening to
// local midnight is what keeps "due today" matching `on_or_after` today for a
// viewer west of UTC -- constructing `new Date('2026-08-14')` directly does not.
export function calendarDayValue(value) {
  const d = toLocalDate(value);
  if (!d) return null;
  return new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
}

// ---------------------------------------------------------------------------
// Tenant chips. Colour is derived from the name so the same customer keeps the
// same chip everywhere it appears, with no palette to maintain per tenant.
// ---------------------------------------------------------------------------
const TENANT_COLORS = [
  { bg: '#dbeafe', text: '#1e40af' }, // blue
  { bg: '#dcfce7', text: '#166534' }, // green
  { bg: '#fef3c7', text: '#92400e' }, // amber
  { bg: '#ede9fe', text: '#5b21b6' }, // violet
  { bg: '#fce7f3', text: '#9d174d' }, // pink
  { bg: '#ccfbf1', text: '#115e59' }, // teal
  { bg: '#ffedd5', text: '#9a3412' }, // orange
  { bg: '#cffafe', text: '#155e75' }, // cyan
  { bg: '#fae8ff', text: '#86198f' }, // fuchsia
];

export function tenantColorFor(name) {
  if (!name) return {};
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = ((hash << 5) - hash) + name.charCodeAt(i);
    hash |= 0;
  }
  const c = TENANT_COLORS[Math.abs(hash) % TENANT_COLORS.length];
  return { backgroundColor: c.bg, color: c.text };
}

// ---------------------------------------------------------------------------
// Sorting — one comparator for the tree and the table.
//
// Both surfaces used to sort independently: Support_List had a dropdown feeding
// its own PRIORITY_ORDER map, and Support_Table re-sorted the same rows again
// with a generic `a[field] ?? ''` string compare. The generic compare is what
// put unset priority and unset due date FIRST ascending -- '' sorts below 'p0'
// and below any ISO date -- so neither sort direction could surface P0 or the
// nearest deadline at the top. Sorting now lives here and runs once.
// ---------------------------------------------------------------------------

// Unset priority sorts AFTER p3, never before p0. prioritySortValue() already
// maps 'none' (and null, via priorityToCode) to 4.
//
// Unset due date sorts as the furthest possible future, so ascending reads
// "earliest deadline first, overdue included, undated last".
export function dueDateSortValue(value) {
  const day = calendarDayValue(value);
  return day === null ? Infinity : day;
}

const STATUS_SORT_ORDER = {
  in_progress: 0, open: 1, approved: 2, draft: 3, waiting: 4,
  done: 5, resolved: 6, closed: 7,
};

function textSortValue(value) {
  return (value ?? '').toString().toLowerCase();
}

function primaryValue(item, field, parentTitle) {
  switch (field) {
    case 'priority': return prioritySortValue(item.priority);
    case 'due_date': return dueDateSortValue(item.due_date);
    case 'status': return STATUS_SORT_ORDER[item.status] ?? 8;
    case 'id': return item.id ?? 0;
    case 'position': return item.position ?? 0;
    case 'created_at': return dueDateSortValue(item.created_at);
    case 'updated_at': return dueDateSortValue(item.updated_at);
    case 'parent': return textSortValue(parentTitle);
    default: return textSortValue(item[field]);
  }
}

// Sort comparator for one field and direction.
//
// `dir` flips the primary key only. The tiebreak chain -- due date, then id --
// always runs ascending, so "priority descending" still reads nearest-deadline
// first within a priority band rather than inverting into a second surprise.
// The id tiebreak makes the order total, so a re-render can never reshuffle
// rows that compare equal.
export function compareWorkItems(a, b, field, dir = 'asc', parentTitles = null) {
  const sign = dir === 'desc' ? -1 : 1;
  const av = primaryValue(a, field, parentTitles?.[a.parent]);
  const bv = primaryValue(b, field, parentTitles?.[b.parent]);

  const isText = typeof av === 'string' || typeof bv === 'string';

  // An empty text cell is the absence of a value, not a value that happens to
  // sort before 'a'. Pin blanks to the bottom in BOTH directions -- returning
  // this before the sign is applied is the whole point. Numeric keys are
  // different: unset priority and unset due date have a defined rank ('none'
  // after p3, undated furthest-future), so they do flip with the direction.
  if (isText) {
    if (!av && bv) return 1;
    if (av && !bv) return -1;
  }

  let primary = 0;
  if (isText) {
    primary = av.localeCompare(bv);
  } else if (av < bv) {
    primary = -1;
  } else if (av > bv) {
    primary = 1;
  }
  if (primary !== 0) return primary * sign;

  // 'position' is manual order, so it tiebreaks on id alone. Rows that have
  // never been dragged all sit at position 0; falling through to the due-date
  // tiebreak would re-rank the entire tree by deadline in the one view whose
  // whole job is the order a human put things in.
  if (field !== 'due_date' && field !== 'position') {
    const ad = dueDateSortValue(a.due_date);
    const bd = dueDateSortValue(b.due_date);
    if (ad !== bd) return ad < bd ? -1 : 1;
  }
  return (a.id ?? 0) - (b.id ?? 0);
}

// The default sort is per view mode, because the two views answer different
// questions and the drag handle only means something in one of them.
//
// Table answers "what do I close next": priority first, nearest deadline inside
// a band. Tree is manual project ordering, so it opens on `position` -- the
// order the drag handle writes. Defaulting the tree to priority made every drag
// a silent no-op: the PATCH succeeded and the row snapped straight back to its
// priority-sorted slot. A visible control that lies is worse than no control.
export const DEFAULT_SORT_BY_MODE = {
  table: { field: 'priority', dir: 'asc' },
  tree: { field: 'position', dir: 'asc' },
};

export function defaultSortFor(viewMode) {
  return DEFAULT_SORT_BY_MODE[viewMode] || DEFAULT_SORT_BY_MODE.table;
}

// ---------------------------------------------------------------------------
// Search matching — single source of truth for both Support_ServiceDesk and
// Support_List so the two surfaces can't drift apart.
//
// Returns a numeric rank (lower = better match, null = no match):
//   0  exact ticket-id match
//   1  id or title substring
//   2  description / requester_name / internal_notes match
//
// Ticket-number queries (#123 or 123) use EXACT matching only, so searching
// "123" does not also return tickets 1234, 2312, etc.
// ---------------------------------------------------------------------------
export function matchesSearch(item, query) {
  if (!query) return 1;
  const q = query.toLowerCase().trim();
  if (!q) return 1;

  // Exact ticket-number query — only match the specific id.
  if (/^#?\d+$/.test(q)) {
    const numericId = q.replace(/^#/, '');
    return String(item.id) === numericId ? 0 : null;
  }

  if (item.title?.toLowerCase().includes(q)) return 1;
  if (String(item.id).includes(q)) return 1;
  if (item.description?.toLowerCase().includes(q)) return 2;
  if (item.requester_name?.toLowerCase().includes(q)) return 2;
  if (item.internal_notes?.toLowerCase().includes(q)) return 2;
  return null;
}

// Collect all descendants of a set of IDs from a flat item list
export function getDescendantIds(itemIds, allItems) {
  const ids = new Set(itemIds);
  let changed = true;
  while (changed) {
    changed = false;
    for (const item of allItems) {
      if (item.parent && ids.has(item.parent) && !ids.has(item.id)) {
        ids.add(item.id);
        changed = true;
      }
    }
  }
  return ids;
}

// Retired localStorage keys from earlier generations of the Support caches.
// Nothing reads them any more, and an item payload is large enough (a Service
// Desk entry measures ~1.8 MB in the browser) that leaving them behind is dead
// weight in a bounded store.
const DEAD_CACHE_KEYS = [
  'service_desk_items',
  'service_desk_v1_filters',
  // Per-tab filter state from the Requirements / Tickets split. The two tabs
  // type-scoped their saved filters, so the same field meant different things
  // in each -- merging them would have produced a view neither user asked for.
  // Both are dropped and the merged list starts clean on support_list_filters.
  'support_list_filters_requirements',
  'support_list_filters_tickets',
];

// Retired cache PREFIXES. These were written as `<prefix>:<slug>:<userId>`, so
// an exact-key sweep never reached them -- every tenant/user a staff member had
// visited left an entry behind. The item payloads are large (a Service Desk
// entry measures ~1.8 MB), so the prefixed form has to be swept too.
const DEAD_CACHE_PREFIXES = [
  'support_list_requirements',
  'support_list_tickets',
  // Pre-store list payloads, superseded by the supportItems Vuex module's
  // versioned prefixes.
  'support_list',
  'service_desk_items',
  'service_desk_items_v2',
];

// Keep a single cached item payload per prefix. Nothing bounds how many
// tenant/user-scoped entries accumulate as staff switch context, so every write
// drops its siblings along with the retired keys above.
export function evictStaleItemCaches(prefix, currentKey) {
  try {
    for (let index = localStorage.length - 1; index >= 0; index--) {
      const key = localStorage.key(index);
      if (!key) continue;
      if (DEAD_CACHE_KEYS.includes(key)) {
        localStorage.removeItem(key);
      } else if (DEAD_CACHE_PREFIXES.some((p) => key === p || key.startsWith(`${p}:`))) {
        localStorage.removeItem(key);
      } else if (key.startsWith(`${prefix}:`) && key !== currentKey) {
        localStorage.removeItem(key);
      }
    }
  } catch {
    // Storage failures are non-fatal, matching BrowserStore: the in-flight
    // response stays the source of truth when localStorage is unavailable.
  }
}
